
import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import {
  ArrowLeft,
  Users,
  MessageCircle,
  MessageSquare,
  Send,
  Bot,
  User,
  Video,
  Mail,
  TrendingUp,
  TrendingDown,
  Minus,
  Slack,
  Calendar,
  Phone,
  FileText,
} from "lucide-react"
import { Input } from "./ui/input"
import { getOrgEqTeamTrackerAggregates, getOrgEqTrackerHistory } from "../../api/helpers"

interface EmotionAnalysis {
  emotion: string
  communication_evidence: string[]
  discussion_topics: string[]
  team_dynamics: string
}

interface TrajectorySummary {
  overall_summary: string
  top_emotions_analysis: EmotionAnalysis[]
  communication_channel_insights: {
    meeting_discussions: string
    email_communications: string
    team_chat_exchanges: string
  }
  leadership_action_items: string[]
}

interface TeamOverview {
  id: string
  name: string
  memberCount: number
  positivityScore: number
  recentEmotions: string[]
  emotionIntensity: number
  trend: "up" | "down" | "stable"
  riskLevel: "improving" | "no change" | "at-risk"
  topTheme: string
  trajectorySummary?: TrajectorySummary | null
}

interface TeamDetailPageProps {
  team: TeamOverview
  organizationId?: string
  period?: "day" | "week" | "month"
  onBack: () => void
}

const sourceIcons = {
  slack: Slack,
  gmail: Mail,
  gcal: Calendar,
  zoom: Video,
  gong: Phone,
  voice: Phone,
  tickets: FileText,
  notes: MessageSquare,
  calls: Video,
  emails: Mail,
  chats: MessageSquare,
}

const getSummarizedReason = (reasoning: any) => {
  if (!reasoning || typeof reasoning !== "object") return null
  const firstKey = Object.keys(reasoning)[0]
  if (!firstKey) return null
  const value = reasoning[firstKey]
  if (value && typeof value === "object") {
    return value.summarized_reason || value.summary || value.description || null
  }
  return null
}

const getHitDescription = (hit: any) => {
  if (!hit) return null
  const reasoningSummary = getSummarizedReason(hit.reasoning)
  const contentDetails = hit.content_details || {}
  const contentSummary =
    hit.summary ||
    hit.description ||
    contentDetails.summary ||
    contentDetails.excerpt ||
    contentDetails.caption ||
    null
  const metadataSummary = hit.metadata?.summary || hit.metadata?.description
  return reasoningSummary || contentSummary || metadataSummary || null
}

const getContentTypeDisplay = (type?: string) => {
  const defaultDisplay = { label: "Message", Icon: MessageSquare }

  if (!type || typeof type !== "string") {
    return defaultDisplay
  }

  const normalized = type.toLowerCase()
  const typeMap: Record<
    string,
    {
      label: string
      icon: keyof typeof sourceIcons
    }
  > = {
    call: { label: "Call", icon: "calls" },
    calls: { label: "Call", icon: "calls" },
    video: { label: "Call", icon: "calls" },
    zoom: { label: "Zoom Call", icon: "zoom" },
    gong: { label: "Call", icon: "gong" },
    voice: { label: "Call", icon: "voice" },
    email: { label: "Email", icon: "emails" },
    emails: { label: "Email", icon: "emails" },
    gmail: { label: "Email", icon: "gmail" },
    slack: { label: "Slack", icon: "slack" },
    chat: { label: "Message", icon: "chats" },
    chats: { label: "Message", icon: "chats" },
    message: { label: "Message", icon: "chats" },
    messages: { label: "Message", icon: "chats" },
    tickets: { label: "Ticket", icon: "tickets" },
    notes: { label: "Note", icon: "notes" },
  }

  const config = typeMap[normalized]
  const Icon = config ? sourceIcons[config.icon] : null

  if (!config || !Icon) {
    return defaultDisplay
  }

  return { label: config.label, Icon }
}

const getHitBorderColorClass = (emotion?: string) => {
  if (!emotion) return "gray"

  const lowerEmotion = emotion.toLowerCase()

  if (
    lowerEmotion.includes("optimism") ||
    lowerEmotion.includes("satisfaction") ||
    lowerEmotion.includes("relief") ||
    lowerEmotion.includes("interest") ||
    lowerEmotion.includes("joy") ||
    lowerEmotion.includes("determined") ||
    lowerEmotion.includes("excited") ||
    lowerEmotion.includes("proud") ||
    lowerEmotion.includes("pride") ||
    lowerEmotion.includes("delight") ||
    lowerEmotion.includes("enthusiasm")
  ) {
    return "green"
  }

  if (
    lowerEmotion.includes("anticipation") ||
    lowerEmotion.includes("curiosity") ||
    lowerEmotion.includes("analytical") ||
    lowerEmotion.includes("empathy") ||
    lowerEmotion.includes("concentration")
  ) {
    return "purple"
  }

  if (
    lowerEmotion.includes("concern") ||
    lowerEmotion.includes("confusion") ||
    lowerEmotion.includes("anxiety") ||
    lowerEmotion.includes("uncertainty") ||
    lowerEmotion.includes("annoyance") ||
    lowerEmotion.includes("anger") ||
    lowerEmotion.includes("distressed") ||
    lowerEmotion.includes("tired") ||
    lowerEmotion.includes("anxious") ||
    lowerEmotion.includes("doubtful") ||
    lowerEmotion.includes("frustrat") ||
    lowerEmotion.includes("stress") ||
    lowerEmotion.includes("disappoint") ||
    lowerEmotion.includes("defensive")
  ) {
    return "red"
  }

  if (lowerEmotion.includes("amusement")) {
    return "orange"
  }

  return "gray"
}

const getHitBorderColor = (emotion?: string) => {
  const colorClass = getHitBorderColorClass(emotion)

  switch (colorClass) {
    case "green":
      return "#22c55e"
    case "purple":
      return "#a855f7"
    case "red":
      return "#ef4444"
    case "orange":
      return "#f97316"
    default:
      return "#9ca3af"
  }
}

const formatTimeAgo = (timestamp?: string) => {
  if (!timestamp || typeof timestamp !== "string") return "Just now"

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return "Just now"

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "Just now"
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
}

// Mock data for team emotional trends over time
const mockTrendData = [
  { week: "W1", optimism: 65, frustration: 25, engagement: 70, event: null },
  { week: "W2", optimism: 45, frustration: 55, engagement: 60, event: "Sprint Planning" },
  { week: "W3", optimism: 75, frustration: 20, engagement: 80, event: "Feature Complete" },
  { week: "W4", optimism: 85, frustration: 15, engagement: 85, event: "Product Launch" },
  { week: "W5", optimism: 90, frustration: 10, engagement: 90, event: "Launch Success" },
]

// Mock data for team members (anonymized/non-anonymized based on governance settings)
const mockTeamMembers = [
  { id: "1", name: "Alex Chen", role: "Senior Engineer", dominantEmotion: "Focused", intensity: 0.8, riskLevel: "low" },
  { id: "2", name: "Sarah Kim", role: "Tech Lead", dominantEmotion: "Optimistic", intensity: 0.9, riskLevel: "low" },
  { id: "3", name: "Mike Johnson", role: "Engineer", dominantEmotion: "Stressed", intensity: 0.6, riskLevel: "medium" },
  {
    id: "4",
    name: "Emma Davis",
    role: "Senior Engineer",
    dominantEmotion: "Engaged",
    intensity: 0.85,
    riskLevel: "low",
  },
  { id: "5", name: "David Wilson", role: "Engineer", dominantEmotion: "Frustrated", intensity: 0.4, riskLevel: "high" },
]

const getEmotionColor = (emotion: string) => {
  const colors = {
    Optimism: "#10b981",
    Engagement: "#06b6d4",
    Frustration: "#ef4444",
    Focus: "#8b5cf6",
    Focused: "#8b5cf6",
    Optimistic: "#10b981",
    Stressed: "#f59e0b",
    Engaged: "#06b6d4",
    Frustrated: "#ef4444",
    Satisfaction: "#10b981",
    Determination: "#8b5cf6",
    Curiosity: "#06b6d4",
    Concentration: "#8b5cf6",
    AestheticAppreciation: "#10b981",
    Contemplation: "#06b6d4",
    Interest: "#06b6d4",
    Anxiety: "#ef4444",
    Distress: "#ef4444",
    Doubt: "#ef4444",
    Anger: "#ef4444",
    Excitement: "#10b981",
    Pride: "#10b981",
    Triumph: "#10b981",
  }
  return colors[emotion as keyof typeof colors] || "#6b7280"
}

const getRiskBadgeColor = (risk: string) => {
  switch (risk) {
    case "high":
      return "bg-red-100 text-red-800 border-red-200"
    case "medium":
      return "bg-yellow-100 text-yellow-800 border-yellow-200"
    case "low":
      return "bg-green-100 text-green-800 border-green-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

const getQualitativeAnalysis = (teamName: string) => {
  const analyses = {
    Engineering: {
      trendAnalysis:
        "The Engineering team shows a strong upward trajectory in optimism and engagement, particularly following the successful product launch in W4. The initial dip in W2 during Sprint Planning reflects typical pre-sprint anxiety, but the team's resilience is evident in their rapid recovery. Frustration levels have consistently decreased, indicating improved workflow processes and reduced technical debt.",
      themeInsights:
        "Sprint Planning remains the dominant theme, correlating with periods of both stress and eventual satisfaction. The team's focus on Technical Discussions and Code Reviews has strengthened collaboration, while Feature Success celebrations have boosted morale significantly. The persistent but manageable frustration around Technical Debt suggests ongoing architectural improvements.",
      recommendations:
        "Continue current sprint practices while addressing remaining technical debt. Consider implementing more frequent celebration of small wins to maintain the positive momentum observed post-launch.",
    },
    Product: {
      trendAnalysis:
        "Product team demonstrates exceptional emotional stability with consistently high optimism levels. The team's engagement peaks align perfectly with feature launch cycles, showing strong product-market alignment motivation. Low frustration levels indicate effective prioritization and stakeholder management.",
      themeInsights:
        "Feature Launch themes dominate positive emotions, while Product Strategy discussions correlate with high engagement. The team's ability to maintain excitement around User Research suggests strong customer empathy. Market Analysis activities show balanced emotional responses, indicating mature decision-making processes.",
      recommendations:
        "Leverage the team's high engagement for mentoring other teams. Consider expanding their role in cross-functional collaboration to spread their positive emotional patterns.",
    },
    Design: {
      trendAnalysis:
        "Design team shows stable but moderate emotional patterns, with creativity and aesthetic appreciation driving positive sentiment. The team maintains consistent engagement levels despite external pressures, indicating strong intrinsic motivation and design passion.",
      themeInsights:
        "Design System work correlates with high satisfaction and pride, while User Experience research drives engagement. The team's emotional responses to Design Reviews are predominantly positive, suggesting healthy critique culture. Brand Guidelines discussions show measured enthusiasm, indicating thoughtful brand stewardship.",
      recommendations:
        "Increase visibility of design impact on business metrics to boost team morale. Consider more frequent design showcases to celebrate creative achievements and maintain momentum.",
    },
    Marketing: {
      trendAnalysis:
        "Marketing team exhibits concerning emotional volatility with high stress and anxiety levels, particularly around campaign deadlines. The team's frustration peaks correlate with external pressure points, suggesting resource constraints or unrealistic timelines affecting wellbeing.",
      themeInsights:
        "Campaign Deadline themes consistently trigger negative emotions, while Creative Strategy discussions show more balanced responses. Social Media activities correlate with moderate engagement, but Brand Messaging work shows signs of decision fatigue. The team's emotional response to Performance Metrics is mixed, indicating unclear success criteria.",
      recommendations:
        "Immediate intervention needed: review campaign timelines and resource allocation. Implement stress management support and consider redistributing workload. Establish clearer success metrics to reduce uncertainty-driven anxiety.",
    },
    Sales: {
      trendAnalysis:
        "Sales team demonstrates strong emotional resilience with high determination and competitive drive. Engagement levels correlate directly with pipeline activity, showing healthy motivation patterns. The team's ability to maintain optimism despite rejection indicates strong mental fortitude.",
      themeInsights:
        "Q4 Targets theme drives both excitement and pressure, creating productive tension. Client Meetings correlate with high engagement and satisfaction, suggesting strong relationship-building skills. Pipeline Management activities show consistent emotional investment, indicating ownership mentality.",
      recommendations:
        "Maintain current momentum while monitoring for burnout signs. Consider implementing peer support systems to sustain high performance levels through challenging periods.",
    },
    Support: {
      trendAnalysis:
        "Support team shows critical emotional distress with consistently high frustration and fatigue levels. The team's emotional state directly correlates with ticket volume, indicating system capacity issues. Declining engagement suggests potential burnout risk requiring immediate attention.",
      themeInsights:
        "Ticket Volume theme dominates negative emotions, while Customer Escalations create additional stress layers. The team's emotional response to Process Improvement initiatives is cautiously optimistic, suggesting openness to change. Knowledge Base activities correlate with slight satisfaction increases, indicating value in documentation efforts.",
      recommendations:
        "Critical intervention required: implement immediate workload relief and process improvements. Consider team expansion or automation solutions. Establish regular check-ins and mental health support resources.",
    },
  }
  return analyses[teamName as keyof typeof analyses] || analyses.Engineering
}

interface TeamEmotion {
  emotion: string
  intensity: number
  themes: string[]
}

const teamEmotions: Record<string, TeamEmotion[]> = {
  Engineering: [
    {
      emotion: "Anxiety",
      intensity: 85,
      themes: [
        "Sprint Planning",
        "API Integration Challenges",
        "Code Reviews",
        "Technical Architecture",
        "Database Optimization",
        "Security Implementation",
        "Performance Tuning",
        "Legacy System Migration",
      ],
    },
    {
      emotion: "Tiredness",
      intensity: 78,
      themes: [
        "Feature Development",
        "Bug Resolution",
        "System Optimization",
        "Technical Debt",
        "Production Incidents",
        "Infrastructure Scaling",
        "Testing Automation",
        "Documentation Updates",
      ],
    },
    {
      emotion: "Distress",
      intensity: 72,
      themes: [
        "Deadline Pressure",
        "On-call Rotation",
        "Complex Debugging",
        "Extended Development Cycles",
        "Critical Bug Fixes",
        "Weekend Deployments",
        "System Outages",
        "Technical Challenges",
      ],
    },
  ],
  Product: [
    {
      emotion: "Excitement",
      intensity: 92,
      themes: [
        "Feature Request Excitement",
        "Product Launch",
        "User Feedback",
        "Market Expansion",
        "Customer Success Stories",
        "Competitive Analysis",
        "Innovation Workshops",
        "Beta Testing Results",
      ],
    },
    {
      emotion: "Pride",
      intensity: 88,
      themes: [
        "Feature Launch Success",
        "User Adoption",
        "Product-Market Fit",
        "Innovation Recognition",
        "Revenue Growth",
        "Customer Testimonials",
        "Industry Awards",
        "Team Achievements",
      ],
    },
    {
      emotion: "Triumph",
      intensity: 85,
      themes: [
        "Milestone Achievement",
        "Competitive Advantage",
        "Revenue Growth",
        "Customer Success",
        "Market Leadership",
        "Product Excellence",
        "Strategic Wins",
        "Vision Realization",
      ],
    },
  ],
  Design: [
    {
      emotion: "Aesthetic Appreciation",
      intensity: 82,
      themes: [
        "Dashboard 2.0 UI",
        "Design System",
        "Brand Guidelines",
        "Visual Consistency",
        "User Interface Polish",
        "Accessibility Standards",
        "Mobile Responsiveness",
        "Design Token Implementation",
      ],
    },
    {
      emotion: "Contemplation",
      intensity: 75,
      themes: [
        "User Experience Research",
        "Design Strategy",
        "Information Architecture",
        "Accessibility",
        "User Journey Mapping",
        "Persona Development",
        "Usability Testing",
        "Design Thinking Workshops",
      ],
    },
    {
      emotion: "Interest",
      intensity: 70,
      themes: [
        "Design Trends",
        "User Testing",
        "Prototype Validation",
        "Creative Exploration",
        "Design Tools",
        "Industry Best Practices",
        "Cross-Platform Design",
        "Emerging Technologies",
      ],
    },
  ],
  Marketing: [
    {
      emotion: "Anxiety",
      intensity: 72,
      themes: [
        "Campaign Deadline",
        "Performance Metrics",
        "Budget Constraints",
        "Market Competition",
        "Lead Generation Targets",
        "Conversion Rate Optimization",
        "Brand Reputation Management",
        "ROI Pressure",
      ],
    },
    {
      emotion: "Distress",
      intensity: 68,
      themes: [
        "Lead Generation",
        "Conversion Rates",
        "Brand Reputation",
        "Resource Allocation",
        "Campaign Performance",
        "Market Positioning",
        "Customer Acquisition Cost",
        "Attribution Modeling",
      ],
    },
    {
      emotion: "Doubt",
      intensity: 55,
      themes: [
        "Strategy Effectiveness",
        "Message Resonance",
        "Channel Performance",
        "ROI Uncertainty",
        "Market Timing",
        "Competitive Response",
        "Creative Direction",
        "Audience Targeting",
      ],
    },
  ],
  Sales: [
    {
      emotion: "Determination",
      intensity: 90,
      themes: [
        "Q4 Targets",
        "Pipeline Management",
        "Client Acquisition",
        "Revenue Goals",
        "Deal Closure",
        "Prospect Qualification",
        "Competitive Positioning",
        "Territory Expansion",
      ],
    },
    {
      emotion: "Excitement",
      intensity: 85,
      themes: [
        "Deal Closure",
        "New Opportunities",
        "Client Relationships",
        "Market Expansion",
        "Product Launches",
        "Partnership Opportunities",
        "Territory Growth",
        "Customer Success",
      ],
    },
    {
      emotion: "Pride",
      intensity: 80,
      themes: [
        "Sales Achievement",
        "Client Success",
        "Team Performance",
        "Revenue Growth",
        "Quota Attainment",
        "Customer Retention",
        "Referral Generation",
        "Market Share Growth",
      ],
    },
  ],
  Support: [
    {
      emotion: "Anger",
      intensity: 65,
      themes: [
        "Support Response Time",
        "System Outages",
        "Escalated Issues",
        "Resource Limitations",
        "Customer Complaints",
        "Process Inefficiencies",
        "Tool Limitations",
        "Knowledge Gaps",
      ],
    },
    {
      emotion: "Distress",
      intensity: 70,
      themes: [
        "Ticket Volume",
        "Customer Complaints",
        "Resolution Delays",
        "Process Inefficiencies",
        "System Downtime",
        "Escalation Management",
        "Customer Satisfaction",
        "Workload Management",
      ],
    },
    {
      emotion: "Tiredness",
      intensity: 60,
      themes: [
        "High Workload",
        "Repetitive Issues",
        "Extended Hours",
        "Burnout Risk",
        "On-call Duties",
        "Complex Troubleshooting",
        "Customer Escalations",
        "Documentation Maintenance",
      ],
    },
  ],
}

const getTeamEmotionData = (teamName: string) => {
  return teamEmotions[teamName as keyof typeof teamEmotions] || teamEmotions.Engineering
}

// Mock data for Key Initiatives
interface Initiative {
  id: string
  name: string
  department: string
  dateRange: string
  emotions: string[]
  positivityScore: number
  trend: "improving" | "no change" | "at-risk"
  hasMessages: boolean
  hasVideo: boolean
}

const mockInitiatives: Record<string, Initiative[]> = {
  Engineering: [
    {
      id: "1",
      name: "Dashboard 2.0 Launch",
      department: "Product & Engineering",
      dateRange: "Nov 1–Dec 15, 2024",
      emotions: ["Pride", "Excitement", "Anxiety"],
      positivityScore: 82,
      trend: "improving",
      hasMessages: true,
      hasVideo: true,
    },
    {
      id: "2",
      name: "Salesforce API Integration",
      department: "Engineering",
      dateRange: "Since Oct 1, 2024",
      emotions: ["Curiosity", "Confidence", "Frustration"],
      positivityScore: 68,
      trend: "no change",
      hasMessages: true,
      hasVideo: false,
    },
    {
      id: "3",
      name: "Benefits Package Redesign",
      department: "HR & Operations",
      dateRange: "Nov 1, 2025–Jan 15, 2026",
      emotions: ["Curiosity", "Satisfaction", "Confusion"],
      positivityScore: 71,
      trend: "improving",
      hasMessages: true,
      hasVideo: false,
    },
    {
      id: "4",
      name: "Q4 Layoffs",
      department: "Organization",
      dateRange: "Nov 10–Nov 15, 2024",
      emotions: ["Anxiety", "Uncertainty", "Sadness"],
      positivityScore: 42,
      trend: "at-risk",
      hasMessages: true,
      hasVideo: true,
    },
    {
      id: "5",
      name: "Jenkins CI/CD Pipeline Automation",
      department: "Engineering",
      dateRange: "Oct 15–Nov 30, 2024",
      emotions: ["Determination", "Pride", "Interest"],
      positivityScore: 76,
      trend: "improving",
      hasMessages: true,
      hasVideo: false,
    },
  ],
  Product: [
    {
      id: "1",
      name: "Dashboard 2.0 Launch",
      department: "Product & Engineering",
      dateRange: "Nov 1–Dec 15, 2024",
      emotions: ["Pride", "Excitement", "Triumph"],
      positivityScore: 92,
      trend: "improving",
      hasMessages: true,
      hasVideo: true,
    },
    {
      id: "2",
      name: "User Research Initiative",
      department: "Product",
      dateRange: "Since Sep 1, 2024",
      emotions: ["Interest", "Contemplation", "Excitement"],
      positivityScore: 85,
      trend: "improving",
      hasMessages: true,
      hasVideo: false,
    },
  ],
  Design: [
    {
      id: "1",
      name: "Design System Overhaul",
      department: "Design",
      dateRange: "Oct 1–Dec 31, 2024",
      emotions: ["Aesthetic Appreciation", "Contemplation", "Frustration"],
      positivityScore: 74,
      trend: "no change",
      hasMessages: true,
      hasVideo: true,
    },
  ],
  Marketing: [
    {
      id: "1",
      name: "Q4 Campaign Launch",
      department: "Marketing",
      dateRange: "Nov 1–Dec 20, 2024",
      emotions: ["Anxiety", "Distress", "Determination"],
      positivityScore: 58,
      trend: "at-risk",
      hasMessages: true,
      hasVideo: true,
    },
  ],
  Sales: [
    {
      id: "1",
      name: "Q4 Sales Targets",
      department: "Sales",
      dateRange: "Oct 1–Dec 31, 2024",
      emotions: ["Determination", "Excitement", "Pride"],
      positivityScore: 88,
      trend: "improving",
      hasMessages: true,
      hasVideo: false,
    },
  ],
  Support: [
    {
      id: "1",
      name: "Support Process Improvements",
      department: "Support",
      dateRange: "Nov 1–Jan 31, 2025",
      emotions: ["Anger", "Distress", "Hope"],
      positivityScore: 48,
      trend: "at-risk",
      hasMessages: true,
      hasVideo: false,
    },
  ],
}

const getTeamInitiatives = (teamName: string): Initiative[] => {
  return mockInitiatives[teamName] || []
}

const getInitiativeEmotionBadgeColor = (emotion: string) => {
  // Hume emotion valence mapping for color coding
  const emotionValence: { [key: string]: number } = {
    Concentration: 0.3,
    Determination: 0.6,
    Satisfaction: 0.9,
    Excitement: 0.9,
    Pride: 0.7,
    Triumph: 0.9,
    "Aesthetic Appreciation": 0.7,
    Contemplation: 0.2,
    Interest: 0.5,
    Hope: 0.5,
    Confidence: 0.6,
    Curiosity: 0.4,
    Anxiety: -0.7,
    Distress: -0.7,
    Doubt: -0.4,
    Anger: -0.8,
    Tiredness: -0.3,
    Frustration: -0.5,
    Uncertainty: -0.5,
    Sadness: -0.6,
    Confusion: -0.3,
  }

  const valence = emotionValence[emotion] || 0

  // Color coding based on valence: red/orange < 0.2, purple -0.2 to 0.3, green > 0.3
  if (valence < -0.2) {
    return "bg-red-100 text-red-800 border-red-200"
  } else if (valence >= -0.2 && valence <= 0.3) {
    return "bg-purple-100 text-purple-800 border-purple-200"
  } else {
    return "bg-green-100 text-green-800 border-green-200"
  }
}

const getInitiativeTrendBadgeColor = (trend: string) => {
  const colors = {
    improving: "bg-green-50 text-green-700 border-green-200",
    "no change": "bg-gray-50 text-gray-700 border-gray-200",
    "at-risk": "bg-red-50 text-red-700 border-red-200",
  }
  return colors[trend as keyof typeof colors] || "bg-gray-50 text-gray-700 border-gray-200"
}

const getInitiativeTrendIcon = (trend: string) => {
  switch (trend) {
    case "improving":
      return <TrendingUp className="h-3 w-3" />
    case "at-risk":
      return <TrendingDown className="h-3 w-3" />
    default:
      return <Minus className="h-3 w-3" />
  }
}

const emotionContexts: Record<string, Record<string, string>> = {
  Engineering: {
    Anxiety:
      "Team members exhibit heightened stress behaviors during API Integration discussions, with Slack threads showing urgent technical conversations and frequent status updates. In sprint planning meetings, engineers demonstrate visible concern about complex architectural decisions and tight deadlines. Code review sessions reveal increased scrutiny and worry about potential issues, with team members expressing concerns about system stability. During database optimization discussions, the team displays anxious problem-solving attitudes, frequently checking and double-checking solutions.",
    Tiredness:
      "Communication patterns show signs of mental fatigue, with shorter responses in Slack and delayed reactions to non-urgent messages. During stand-up meetings, team members exhibit lower energy levels, speaking more quietly and providing brief updates. Chat threads reveal increased typos and less detailed explanations than usual, indicating cognitive load. However, the team maintains supportive attitudes, with members checking in on each other's wellbeing and offering to help with complex debugging tasks.",
    Distress:
      "Strong emotional strain emerges in team communications during production incidents, with engineers expressing frustration about system failures and time pressures. Slack conversations during system outages show stressed troubleshooting patterns, with team members working extended hours under pressure. In retrospective meetings, the team demonstrates concern about recurring issues and technical debt accumulation. Critical bug discussions reveal underlying worry about code quality and system reliability, with engineers advocating for more thorough testing despite time constraints.",
  },
  Product: {
    Excitement:
      "Team communications burst with enthusiasm during feature discussions, with multiple exclamation points and positive emojis in Slack channels. Meeting recordings capture animated voices and rapid-fire idea generation, especially when discussing user feedback. Beta testing conversations show genuine curiosity and eagerness to iterate, with team members actively seeking out user insights. Product launch discussions reveal infectious optimism, with team members expressing pride in their work and confidence in market reception.",
    Pride:
      "Deep satisfaction emerges in team retrospectives, with members reflecting thoughtfully on achievements and expressing gratitude for collaborative efforts. Customer success story discussions generate visible emotional responses, with team members sharing personal connections to user outcomes. Industry recognition conversations show humble confidence, with the team acknowledging their hard work while crediting cross-functional partnerships. Product-market fit validation discussions reveal quiet satisfaction and validation of strategic decisions.",
    Triumph:
      "Victory celebrations in team channels show genuine joy and mutual appreciation, with team members highlighting each other's contributions. Competitive advantage discussions reveal strategic thinking and confident positioning, with the team expressing belief in their unique value proposition. Revenue milestone conversations demonstrate understanding of business impact, with team members connecting their product decisions to company success. Market leadership discussions show mature perspective on responsibility and continued innovation.",
  },
  Design: {
    "Aesthetic Appreciation":
      "Design critique sessions reveal passionate discussions about visual harmony and user experience, with team members expressing genuine care for aesthetic details. Dashboard feedback conversations show pride in craftsmanship, with designers explaining their creative decisions and rationale. Brand guideline discussions demonstrate commitment to consistency, with team members advocating for design standards across all touchpoints. User interface polish conversations reveal perfectionist tendencies balanced with practical considerations.",
    Contemplation:
      "User research discussions show deep empathy and thoughtful analysis, with team members asking probing questions about user motivations and pain points. Information architecture conversations reveal systematic thinking, with designers carefully considering user mental models and navigation patterns. Accessibility discussions demonstrate inclusive mindset, with team members advocating for users with diverse needs. Design strategy meetings show long-term thinking and consideration of broader business implications.",
    Interest:
      "Design trend conversations spark curiosity and exploration, with team members sharing inspiration and discussing potential applications. User testing sessions generate genuine fascination with user behavior, leading to insightful observations and design iterations. Prototype validation discussions show experimental mindset, with designers embracing feedback and iteration. Creative exploration conversations reveal passion for innovation and willingness to challenge conventional approaches.",
  },
  Marketing: {
    Anxiety:
      "Campaign deadline discussions reveal stress patterns in communication, with team members expressing concerns about timeline feasibility and resource constraints. Performance metric conversations show worry about meeting targets, with frequent check-ins and status updates indicating heightened vigilance. Budget constraint discussions demonstrate careful consideration of every decision, sometimes leading to analysis paralysis. Competitive pressure conversations reveal defensive attitudes and urgency to respond quickly.",
    Distress:
      "Lead generation conversations show frustration with current performance, with team members expressing disappointment and seeking alternative strategies. Conversion rate discussions reveal concern about optimization efforts, with the team questioning existing approaches and seeking new solutions. Brand reputation conversations demonstrate protective attitudes, with team members monitoring mentions and responding defensively to criticism. Resource allocation discussions show tension and competing priorities affecting team dynamics.",
    Doubt:
      "Strategy effectiveness conversations reveal uncertainty and second-guessing, with team members questioning previous decisions and seeking validation. Message resonance discussions show lack of confidence in creative direction, with multiple revisions and hesitation to commit to campaigns. Channel performance conversations demonstrate confusion about attribution and effectiveness, leading to scattered focus. Market timing discussions reveal anxiety about external factors beyond team control.",
  },
  Sales: {
    Determination:
      "Quota pursuit conversations show unwavering commitment and competitive spirit, with team members sharing strategies and motivating each other. Pipeline management discussions reveal disciplined approach and attention to detail, with systematic tracking and follow-up behaviors. Client acquisition conversations demonstrate persistence and resilience, with team members learning from rejections and adapting approaches. Revenue goal discussions show ownership mentality and personal investment in company success.",
    Excitement:
      "Deal closure conversations burst with energy and anticipation, with team members celebrating progress and sharing success stories. New opportunity discussions generate enthusiasm and collaborative problem-solving, with the team brainstorming creative approaches. Client relationship conversations show genuine care for customer success, with team members expressing satisfaction from helping clients achieve their goals. Market expansion discussions reveal optimism and confidence in growth potential.",
  },
  Pride: {
    Achievement:
      "Achievement conversations demonstrate quiet confidence and satisfaction, with team members acknowledging hard work and celebrating milestones. Client success discussions show personal investment in customer outcomes, with team members taking ownership of client achievements. Performance recognition conversations reveal humility and team-first attitudes, with members crediting collaborative efforts. Revenue contribution discussions show understanding of business impact and pride in organizational success.",
  },
  Support: {
    Anger:
      "System outage conversations reveal frustration with inadequate tools and processes, with team members expressing concern about customer impact. Response time discussions show defensive attitudes about SLA performance, with the team feeling pressure from unrealistic expectations. Customer complaint conversations demonstrate emotional investment in customer satisfaction, sometimes leading to personal frustration when unable to help effectively. Resource limitation discussions show advocacy for team needs and customer service quality.",
    Distress:
      "Ticket volume conversations reveal overwhelm and concern about service quality, with team members expressing worry about customer satisfaction. Resolution delay discussions show guilt and frustration about not meeting customer expectations, with the team seeking solutions despite constraints. Escalation management conversations demonstrate stress about complex issues and time pressures. Customer satisfaction discussions reveal personal investment in service quality and disappointment with current performance.",
    Tiredness:
      "Workload conversations show signs of burnout and fatigue, with team members expressing need for support and process improvements. On-call discussions reveal exhaustion from constant availability, with the team seeking better work-life balance. Complex troubleshooting conversations show mental fatigue but continued commitment to problem resolution. Burnout risk discussions demonstrate team awareness of wellbeing issues and need for organizational support.",
  },
}

const getTeamSpecificContext = (emotion: string, teamName: string) => {
  const teamContext = emotionContexts[teamName as keyof typeof emotionContexts]
  return (
    teamContext?.[emotion as keyof typeof teamContext] ||
    "This emotion reflects the team's current state and requires monitoring for optimal performance and wellbeing."
  )
}

const getActionRecommendations = (teamName: string, riskLevel: string) => {
  const recommendations = {
    Engineering: {
      high: [
        "Implement immediate 1:1s focusing on workload distribution and technical debt stress - schedule within 48 hours",
        "Introduce 'No Meeting Fridays' to provide uninterrupted coding time and reduce context switching fatigue",
        "Create rotating on-call schedule to prevent burnout from API Integration Challenges and system monitoring",
        "Establish 'Code Review Buddy System' to distribute knowledge and reduce individual pressure on complex features",
        "Organize team lunch-and-learn sessions to address technical challenges collaboratively rather than in isolation",
        "Implement 'Definition of Done' boundaries to prevent scope creep during sprint execution",
      ],
      medium: [
        "Schedule bi-weekly 'Technical Wellness' check-ins to discuss code quality concerns and process improvements",
        "Introduce pair programming sessions for complex API integrations to reduce individual cognitive load",
        "Create documentation sprints to reduce knowledge silos and onboarding stress for new team members",
        "Establish 'Innovation Time' (20% time) for exploring new technologies and reducing routine work monotony",
        "Implement team retrospectives focused on process optimization and developer experience improvements",
        "Set up cross-team collaboration sessions with Product team to improve requirement clarity",
      ],
      low: [
        "Leverage team's strong concentration patterns by creating 'Deep Work' blocks in team calendar",
        "Establish mentorship program where senior engineers guide junior developers on complex architectural decisions",
        "Create technical blog or internal wiki to share successful problem-solving approaches across organization",
        "Organize 'Demo Days' to showcase technical achievements and boost team pride in their work",
        "Implement 'Failure Parties' to celebrate learning from production incidents and normalize mistake-making",
        "Provide conference attendance opportunities to maintain engagement with latest industry practices",
      ],
    },
    Product: {
      high: [
        "Conduct immediate stakeholder alignment sessions to clarify conflicting priorities and reduce decision paralysis",
        "Implement 'Product Health Checks' with weekly 1:1s focusing on workload and strategic clarity",
        "Create cross-functional working groups to improve communication between Product, Engineering, and Design",
        "Establish clear escalation paths for feature requests to prevent team from feeling overwhelmed by competing demands",
        "Introduce 'Product Strategy Sessions' to ensure team alignment on long-term vision and reduce uncertainty",
        "Implement user feedback triage system to help team focus on highest-impact customer needs",
      ],
      medium: [
        "Schedule regular 'Product Sync' meetings with Engineering to improve feature development collaboration",
        "Create customer interview rotation so all team members maintain direct user connection",
        "Implement OKR review sessions to ensure team understands how their work connects to business outcomes",
        "Establish 'Product Critique' sessions similar to design reviews to improve decision-making processes",
        "Organize cross-team shadowing opportunities to understand downstream impact of product decisions",
        "Create product success celebration rituals to maintain team motivation during long development cycles",
      ],
      low: [
        "Leverage team's excitement by creating 'Product Innovation Labs' for exploring new feature concepts",
        "Establish Product team as internal consultants for other teams on user-centered design thinking",
        "Create customer success story sharing sessions to maintain team connection to user impact",
        "Implement 'Product Leadership Development' program for team members interested in advancing",
        "Organize industry conference presentations to share successful product strategies externally",
        "Create product metrics dashboard that team can use to track and celebrate their impact",
      ],
    },
    Design: {
      high: [
        "Implement 'Creative Wellness' check-ins to address design critique fatigue and creative blocks",
        "Create protected 'Design Thinking Time' free from meetings and interruptions for deep creative work",
        "Establish design peer review system to distribute feedback load and reduce individual criticism sensitivity",
        "Organize 'Design Inspiration Sessions' with external speakers to re-energize creative thinking",
        "Implement user testing rotation to ensure all designers maintain connection to user feedback",
        "Create design system governance committee to reduce individual decision-making burden",
      ],
      medium: [
        "Schedule regular 'Design Impact Reviews' to connect design decisions to business and user outcomes",
        "Implement cross-functional design workshops to improve collaboration with Product and Engineering",
        "Create design portfolio development time for team members to document and reflect on their work",
        "Establish 'Design Critique Best Practices' training to improve feedback culture and reduce defensiveness",
        "Organize design tool training sessions to improve efficiency and reduce technical frustration",
        "Create design career development paths with clear advancement criteria and skill development plans",
      ],
      low: [
        "Leverage team's aesthetic appreciation by creating design showcase events for broader organization",
        "Establish Design team as design thinking facilitators for other departments",
        "Create design mentorship program pairing experienced designers with other teams",
        "Implement 'Design Excellence Awards' to recognize outstanding creative work and maintain high standards",
        "Organize design conference attendance and speaking opportunities to share expertise externally",
        "Create design research initiatives that team can lead to explore new user experience territories",
      ],
    },
    Marketing: {
      high: [
        "Implement immediate workload audit and redistribute campaign responsibilities to prevent individual burnout",
        "Create 'Campaign Stress Management' protocols including realistic timeline setting and scope boundaries",
        "Establish weekly 1:1s focused on anxiety management and providing emotional support during high-pressure periods",
        "Introduce 'Marketing Wellness Wednesdays' with stress reduction activities and team building exercises",
        "Create campaign post-mortem processes that focus on learning rather than blame to reduce performance anxiety",
        "Implement 'Marketing Success Metrics' clarity sessions to reduce uncertainty about performance expectations",
      ],
      medium: [
        "Schedule bi-weekly 'Campaign Confidence Building' sessions to review successful strategies and build team morale",
        "Create cross-functional collaboration workshops with Sales team to improve lead quality and reduce conversion pressure",
        "Implement marketing automation training to reduce manual work stress and improve efficiency",
        "Establish 'Marketing Innovation Time' for exploring new channels and creative approaches without pressure",
        "Create peer mentoring system where team members support each other through challenging campaigns",
        "Organize customer feedback sessions to help team understand impact and reduce doubt about message effectiveness",
      ],
      low: [
        "Leverage team's creative energy by establishing Marketing as internal brand consultants for other departments",
        "Create marketing excellence documentation to share successful campaign strategies across organization",
        "Implement 'Marketing Leadership Development' program for team members ready for increased responsibilities",
        "Organize industry conference presentations to showcase marketing innovations and build team reputation",
        "Create marketing metrics celebration rituals to maintain motivation and recognize achievements",
        "Establish marketing research initiatives to explore new market opportunities and maintain engagement",
      ],
    },
    Sales: {
      high: [
        "Implement immediate sales coaching sessions focusing on deal strategy and confidence building",
        "Create 'Sales Resilience Training' to help team members manage rejection and maintain motivation",
        "Establish peer support groups where team members share challenges and successful strategies",
        "Introduce flexible quota arrangements or timeline adjustments to reduce individual pressure",
        "Create sales process optimization workshops to identify and remove friction points",
        "Implement 'Sales Wellness Checks' with focus on mental health and work-life balance during high-pressure periods",
      ],
      medium: [
        "Optimize sales processes to reduce administrative burden",
        "Provide additional market intelligence and competitive insights",
        "Implement team collaboration tools for knowledge sharing",
        "Review and improve lead qualification processes",
      ],
      low: [
        "Recognize top performers and share success strategies",
        "Expand successful team members' roles in training others",
        "Provide advanced sales training or conference opportunities",
        "Consider leadership development opportunities",
      ],
    },
    Support: {
      high: [
        "Implement immediate workload relief through temporary staff augmentation or ticket triage system",
        "Create 'Support Team Wellness Program' with stress management resources and mental health support",
        "Establish daily team huddles to provide emotional support and collaborative problem-solving for difficult cases",
        "Introduce 'Customer Escalation Support System' where team members can quickly get help with complex issues",
        "Create 'Support Hero Recognition' program to acknowledge difficult work and maintain team morale",
        "Implement 'Support Process Improvement Sprints' to address systemic issues causing team frustration",
      ],
      medium: [
        "Schedule weekly 'Support Team Retrospectives' to identify process improvements and celebrate successes",
        "Create knowledge sharing sessions to distribute expertise and reduce individual burden on complex issues",
        "Implement customer feedback sharing to help team understand positive impact of their work",
        "Establish 'Support Skill Development' program to provide career growth opportunities within support",
        "Create cross-team collaboration sessions with Product team to improve product usability and reduce support burden",
        "Organize 'Support Excellence Training' focusing on difficult conversation management and de-escalation techniques",
      ],
      low: [
        "Leverage team's customer service expertise by creating support best practices documentation for organization",
        "Establish Support team as customer experience consultants for Product and Engineering teams",
        "Create 'Support Innovation Lab' for developing new customer service approaches and tools",
        "Implement 'Support Leadership Development' program for team members interested in advancing",
        "Organize customer service conference participation to maintain professional development and industry connection",
        "Create support metrics celebration system to recognize team achievements and maintain positive momentum",
      ],
    },
  }

  const teamRecs = recommendations[teamName as keyof typeof recommendations]
  if (!teamRecs) return []
  return teamRecs[riskLevel as keyof typeof teamRecs] || []
}

const getTeamCommStats = (teamName: string) => {
  const commStats: Record<
    string,
    {
      meetings: { total: number; monthlyAvg: number }
      chat: { total: number; monthlyAvg: number }
      email: { total: number; monthlyAvg: number }
      avgResponse: string
    }
  > = {
    Engineering: {
      meetings: { total: 1350, monthlyAvg: 1420 },
      chat: { total: 2400, monthlyAvg: 2650 },
      email: { total: 180, monthlyAvg: 195 },
      avgResponse: "2.3h",
    },
    Product: {
      meetings: { total: 1650, monthlyAvg: 1750 },
      chat: { total: 1800, monthlyAvg: 1900 },
      email: { total: 150, monthlyAvg: 165 },
      avgResponse: "1.8h",
    },
    Design: {
      meetings: { total: 1050, monthlyAvg: 1150 },
      chat: { total: 3000, monthlyAvg: 3200 },
      email: { total: 120, monthlyAvg: 135 },
      avgResponse: "3.1h",
    },
    Marketing: {
      meetings: { total: 1200, monthlyAvg: 1280 },
      chat: { total: 2100, monthlyAvg: 2250 },
      email: { total: 300, monthlyAvg: 320 },
      avgResponse: "4.2h",
    },
    Sales: {
      meetings: { total: 1800, monthlyAvg: 1900 },
      chat: { total: 1500, monthlyAvg: 1600 },
      email: { total: 180, monthlyAvg: 195 },
      avgResponse: "1.2h",
    },
    Support: {
      meetings: { total: 750, monthlyAvg: 820 },
      chat: { total: 3900, monthlyAvg: 4100 },
      email: { total: 90, monthlyAvg: 105 },
      avgResponse: "45m",
    },
  }
  return (
    commStats[teamName] || {
      meetings: { total: 1200, monthlyAvg: 1280 },
      chat: { total: 2400, monthlyAvg: 2550 },
      email: { total: 180, monthlyAvg: 195 },
      avgResponse: "2h",
    }
  )
}

interface HeatmapNode {
  id: string
  label: string
  sublabel: string
  color: string
  icon: string
  score?: number
  position: { x: number; y: number }
  details: {
    title: string
    metrics: Array<{
      time: string
      label: string
      value: string
      description: string
      icon: string
    }>
    suggestedAction: string
  }
}

const getTeamScore = (teamName: string) => {
  const teamScores = {
    Engineering: 75,
    Product: 85,
    Design: 72,
    Marketing: 58,
    Sales: 82,
    Support: 45,
  }
  return teamScores[teamName as keyof typeof teamScores] || 70
}

const getSentimentBadgeColor = (sentiment: string) => {
  const colorMap = {
    Positive: "bg-green-50 text-green-800",
    Negative: "bg-red-50 text-red-800",
    Neutral: "bg-gray-50 text-gray-800",
    Concerned: "bg-orange-50 text-orange-800",
  }
  return colorMap[sentiment as keyof typeof colorMap] || "bg-gray-50 text-gray-800"
}

function TeamHeatmapVisualization({ team }: { team: any }) {
  const [selectedNode, setSelectedNode] = useState<string | null>("center")
  const [showActionPlan, setShowActionPlan] = useState(false)

  const getTeamHeatmapData = (teamName: string): HeatmapNode[] => {
    const radius = 100 // Further reduced radius for closer spacing
    const centerX = 50 // Center at 50% of container
    const centerY = 50 // Center at 50% of container

    // Calculate positions for 6 nodes evenly distributed in a circle
    const calculatePosition = (index: number, total: number) => {
      const angle = (index * 2 * Math.PI) / total - Math.PI / 2 // Start from top
      return {
        x: centerX + (radius * Math.cos(angle)) / 2.5, // Increased divisor for tighter positioning
        y: centerY + (radius * Math.sin(angle)) / 2.5, // Increased divisor for tighter positioning
      }
    }

    const baseNodes = [
      {
        id: "collaboration",
        label: "Collaboration",
        sublabel: "Dynamics",
        color: "bg-blue-50 border-blue-200 text-blue-800",
        icon: "🤝",
        position: calculatePosition(0, 6),
        details: {
          title: "Collaboration Dynamics",
          metrics: [
            {
              time: "W1",
              label: "Meeting Attendance",
              value: "95%",
              description: "High attendance in team meetings",
              icon: "calendar",
            },
            {
              time: "W2",
              label: "Cross-functional Projects",
              value: "3",
              description: "Number of cross-functional projects",
              icon: "users",
            },
          ],
          suggestedAction:
            "Formalize knowledge sharing sessions; create cross-training opportunities to strengthen collaboration further.",
        },
      },
      {
        id: "workload",
        label: "Workload &",
        sublabel: "Stress",
        color: "bg-orange-50 border-orange-200 text-orange-800",
        icon: "⚠️",
        position: calculatePosition(1, 6),
        details: {
          title: "Workload & Stress",
          metrics: [
            {
              time: "W1",
              label: "Hours Worked",
              value: "45",
              description: "Average hours worked per week",
              icon: "clock",
            },
            {
              time: "W2",
              label: "Project Deadlines",
              value: "2",
              description: "Number of project deadlines this week",
              icon: "calendar",
            },
          ],
          suggestedAction:
            "Implement workload monitoring; schedule one-on-ones to address stress factors and redistribute tasks.",
        },
      },
      {
        id: "trust",
        label: "Trust &",
        sublabel: "Psychological Safety",
        color: "bg-green-50 border-green-200 text-green-800",
        icon: "🛡️",
        position: calculatePosition(2, 6),
        details: {
          title: "Trust & Psychological Safety",
          metrics: [
            {
              time: "W1",
              label: "Feedback Received",
              value: "5",
              description: "Number of feedback instances received",
              icon: "message-circle",
            },
            {
              time: "W2",
              label: "Open Discussions",
              value: "3",
              description: "Number of open discussions initiated",
              icon: "message-square",
            },
          ],
          suggestedAction: "Maintain this positive culture; consider team as model for psychological safety practices.",
        },
      },
      {
        id: "reliability",
        label: "Reliability &",
        sublabel: "Delivery",
        color: "bg-blue-50 border-blue-200 text-blue-800",
        icon: "✅",
        position: calculatePosition(3, 6),
        details: {
          title: "Reliability & Delivery",
          metrics: [
            {
              time: "W1",
              label: "Tasks Completed",
              value: "10",
              description: "Number of tasks completed this week",
              icon: "check-square",
            },
            {
              time: "W2",
              label: "Project Milestones",
              value: "2",
              description: "Number of project milestones achieved",
              icon: "flag",
            },
          ],
          suggestedAction: "Document and share this team's delivery practices as best practices for other teams.",
        },
      },
      {
        id: "engagement",
        label: "Engagement &",
        sublabel: "Energy",
        color: "bg-purple-50 border-purple-200 text-purple-800",
        icon: "⚡",
        position: calculatePosition(4, 6),
        details: {
          title: "Engagement & Energy",
          metrics: [
            {
              time: "W1",
              label: "Voluntary Contributions",
              value: "4",
              description: "Number of voluntary contributions made",
              icon: "heart",
            },
            {
              time: "W2",
              label: "Innovation Ideas",
              value: "2",
              description: "Number of innovation ideas proposed",
              icon: "lightbulb",
            },
          ],
          suggestedAction:
            "Channel this high engagement into innovation projects; provide opportunities for skill development.",
        },
      },
      {
        id: "alignment",
        label: "Alignment &",
        sublabel: "Purpose",
        color: "bg-teal-50 border-teal-200 text-teal-800",
        icon: "🎯",
        position: calculatePosition(5, 6),
        details: {
          title: "Alignment & Purpose",
          metrics: [
            {
              time: "W1",
              label: "Goal Discussions",
              value: "3",
              description: "Number of goal-related discussions",
              icon: "target",
            },
            {
              time: "W2",
              label: "Strategic Planning",
              value: "2",
              description: "Number of strategic planning sessions",
              icon: "map",
            },
          ],
          suggestedAction:
            "Reinforce this alignment through regular goal-setting sessions; involve team in strategic planning.",
        },
      },
    ]

    // Customize based on team type
    if (teamName === "Marketing") {
      baseNodes[1].label = "Campaign"
      baseNodes[1].sublabel = "Pressure"
      baseNodes[4].label = "Creative"
      baseNodes[4].sublabel = "Energy"
    } else if (teamName === "Support") {
      baseNodes[1].label = "Ticket"
      baseNodes[1].sublabel = "Volume"
      baseNodes[3].label = "Customer"
      baseNodes[3].sublabel = "Success"
    }

    return baseNodes
  }

  const heatmapNodes = team ? getTeamHeatmapData(team.name) : []
  const selectedNodeData =
    selectedNode && heatmapNodes.length > 0 ? heatmapNodes.find((n) => n.id === selectedNode) : null

  const getEmotionBadgeColor = (emotion: string) => {
    const colorMap = {
      Excitement: "bg-yellow-50 text-yellow-800",
      Pride: "bg-green-50 text-green-800",
      Triumph: "bg-blue-50 text-blue-800",
      Anxiety: "bg-red-50 text-red-800",
      Determination: "bg-purple-50 text-purple-800",
      Concentration: "bg-indigo-50 text-indigo-800",
    }
    return colorMap[emotion as keyof typeof colorMap] || "bg-gray-50 text-gray-800"
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Heatmap Visualization */}
      <div className="lg:col-span-2">
        <Card className="p-8 bg-white">
          <div className="relative w-full h-[500px] bg-white rounded-lg border border-gray-100">
            {/* Center Emotional Climate Circle */}
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 cursor-pointer"
              onClick={() => setSelectedNode(selectedNode === "center" ? null : "center")}
            >
              <div
                className={`w-44 h-44 rounded-full bg-white border-4 border-[#6FC276] flex flex-col items-center justify-center shadow-lg transition-all duration-300 hover:scale-105 ${
                  selectedNode === "center" ? "ring-3 ring-[#6FC276] scale-105" : ""
                }`}
              >
                <div className="text-sm font-medium text-gray-500 mb-1">Emotional Climate</div>
                <div className="text-xl font-bold text-gray-800">{team.name}</div>
                <div className="text-lg font-bold text-[#6FC276] flex items-center gap-1">
                  {getTeamScore(team.name)}% Positive
                  <span className="text-sm">📈</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">trending up</div>
              </div>
            </div>

            {/* Six Organizational Pillars */}
            {heatmapNodes &&
              heatmapNodes.length > 0 &&
              heatmapNodes.map((node) => (
                <div
                  key={node.id}
                  className={`absolute w-24 h-24 rounded-full border-2 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg z-20 ${
                    selectedNode === node.id ? "ring-3 ring-[#6FC276] scale-105 shadow-lg" : "shadow-sm"
                  } ${node.color} bg-white`}
                  style={{
                    left: `${node.position.x}%`,
                    top: `${node.position.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                  onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
                >
                  <div className="text-xl mb-1">{node.icon}</div>
                  <div className="text-xs font-semibold text-center leading-tight px-2">{node.label}</div>
                  <div className="text-xs font-semibold text-center leading-tight px-2 opacity-75">{node.sublabel}</div>
                </div>
              ))}

            {/* Connecting Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
              {heatmapNodes &&
                heatmapNodes.length > 0 &&
                heatmapNodes.map((node) => (
                  <line
                    key={`line-${node.id}`}
                    x1="50%"
                    y1="50%"
                    x2={`${node.position.x}%`}
                    y2={`${node.position.y}%`}
                    stroke="#6FC276"
                    strokeWidth="2"
                    strokeDasharray="8,4"
                    opacity="0.3"
                  />
                ))}
            </svg>
          </div>
        </Card>
      </div>

      {/* Right Sidebar */}
      <div className="lg:col-span-1">
        <Card className="p-6 h-full">
          {selectedNode === "center" ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#6FC276]"></div>
                <h3 className="text-lg font-semibold">Overall Team Climate</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Current Emotional State</h4>
                  <p className="text-sm leading-relaxed">
                    The {team.name} team is experiencing a strong positive emotional climate with{" "}
                    {getTeamScore(team.name)}% positivity. Team members are demonstrating high levels of excitement and
                    pride around recent product launches, with consistent triumph emotions emerging from successful
                    milestone completions.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Trending Emotions</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {getTeamEmotionData(team?.name || "")
                      ?.slice(0, 3)
                      ?.map((emotion, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className={`text-xs ${getEmotionBadgeColor(emotion.emotion)}`}
                        >
                          {emotion.emotion}
                        </Badge>
                      )) || []}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Primary emotions driving team dynamics include excitement from feature launches, pride in delivery
                    quality, and triumph from overcoming technical challenges.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Contributing Trackers</h4>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Feature Request Excitement:</span> High engagement with upcoming
                      releases
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Dashboard 2.0 UI:</span> Positive feedback on interface improvements
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">API Integration:</span> Successful completion of complex
                      integrations
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Team Dynamics Summary</h4>
                  <p className="text-sm leading-relaxed">
                    Communication patterns show increased collaboration frequency with 15% more cross-functional
                    meetings. The team exhibits strong psychological safety with open discussion of challenges and
                    proactive problem-solving behaviors in daily standups and retrospectives.
                  </p>
                </div>
              </div>
            </>
          ) : selectedNode ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-gray-800">{selectedNodeData?.details?.title}</h3>
              </div>

              <div className="space-y-4">

                <h4 className="text-sm font-semibold text-gray-600 border-b pb-1">Metrics</h4>
                <div className="space-y-2">
                  {selectedNodeData?.details?.metrics?.map((metric, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                        <span className="text-sm font-bold text-gray-900">{metric.value}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{metric.description}</p>
                    </div>
                  )) || []}
                </div>
              </div>

              {/* Suggested Actions */}
              <div
                className="pt-4 border-t border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors duration-200 rounded-lg p-3 -m-3"
                onClick={() => setShowActionPlan(true)}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-gray-700">Suggested Actions</span>
                  <Badge variant="outline" className="text-xs border-[#6FC276] text-[#6FC276]">
                    Manager Nudge
                  </Badge>
                  <div className="ml-auto">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4L5 20l.94-5.66a8.003 8.003 0 010-2.68L5 4l1-4a8.013 8.013 0 017-4c4.418 0 8 3.582 8 8z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-sm text-gray-700 bg-[#6FC276]/10 p-3 rounded-lg border border-[#6FC276]/20 hover:bg-[#6FC276]/15 transition-colors duration-200">
                  {selectedNodeData?.details?.suggestedAction}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <div className="text-4xl">🎯</div>
              <h3 className="text-lg font-semibold text-gray-700">Select a Pillar</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Click on any of the six organizational pillars to view detailed insights, current team state, and
                suggested management actions.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export function TeamDetailPage({ team, organizationId, period = "day", onBack }: TeamDetailPageProps) {
  const [showAnonymized, setShowAnonymized] = useState(true) // This would be controlled by governance settings
  const [showActionPlan, setShowActionPlan] = useState(false)
  const [selectedTeamComm, setSelectedTeamComm] = useState<any>(null)
  const [trackerAggregates, setTrackerAggregates] = useState<any[]>([])
  const [loadingTrackers, setLoadingTrackers] = useState(false)
  const [chatMessages, setChatMessages] = useState([
    {
      type: "bot",
      message: `Hi! I'm here to help you create an action plan for the ${team.name} team. Based on their current emotional state and recent patterns, I can suggest specific actions to ${team.riskLevel === "at-risk" ? "address concerns and improve team wellbeing" : "maintain positive momentum and enhance productivity"}.`,
    },
  ])
  const [userInput, setUserInput] = useState("")
  const [expandedInitiativeId, setExpandedInitiativeId] = useState<string | null>(null)
  const [initiativeHits, setInitiativeHits] = useState<Record<string, any[]>>({})
  const [initiativeLoading, setInitiativeLoading] = useState<Record<string, boolean>>({})
  const [initiativeError, setInitiativeError] = useState<Record<string, string | null>>({})

  const qualitativeData = getQualitativeAnalysis(team.name)
  const teamEmotionData = getTeamEmotionData(team.name)

  // Fetch tracker aggregates for this team
  useEffect(() => {
    const fetchTrackerAggregates = async () => {
      if (!organizationId) {
        console.log("[TeamDetailPage] No organization_id, using mock data")
        return
      }

      setLoadingTrackers(true)
      try {
        const response = await getOrgEqTeamTrackerAggregates(organizationId, period)
        console.log("[TeamDetailPage] Tracker aggregates response:", response)

        if (response?.success && response?.team_aggregates) {
          // Filter for this specific team
          const teamTrackers = response.team_aggregates.filter((agg: any) => agg.team_id === team.id)
          setTrackerAggregates(teamTrackers)
          console.log("[TeamDetailPage] Team tracker aggregates:", teamTrackers)
        }
      } catch (error) {
        console.error("[TeamDetailPage] Failed to fetch tracker aggregates:", error)
      } finally {
        setLoadingTrackers(false)
      }
    }

    fetchTrackerAggregates()
  }, [organizationId, team.id, period])

  const handleInitiativeClick = async (initiativeId: string, trackerId?: string | null) => {
    if (expandedInitiativeId === initiativeId) {
      setExpandedInitiativeId(null)
      return
    }

    setExpandedInitiativeId(initiativeId)

    if (!trackerId || !organizationId) {
      return
    }

    if (initiativeHits[initiativeId] || initiativeLoading[initiativeId]) {
      return
    }

    setInitiativeLoading((prev) => ({ ...prev, [initiativeId]: true }))
    setInitiativeError((prev) => ({ ...prev, [initiativeId]: null }))

    try {
      const response = await getOrgEqTrackerHistory(organizationId, trackerId, 3)
      const hits =
        response?.recent_hits ||
        response?.hits ||
        response?.history ||
        response?.data ||
        []

      setInitiativeHits((prev) => ({ ...prev, [initiativeId]: hits }))
    } catch (error: any) {
      console.error("[TeamDetailPage] Failed to load tracker hits for initiative:", error)
      setInitiativeError((prev) => ({
        ...prev,
        [initiativeId]: error?.response?.data?.message || "Unable to load tracker insights right now.",
      }))
    } finally {
      setInitiativeLoading((prev) => ({ ...prev, [initiativeId]: false }))
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600"
    if (score >= 70) return "text-yellow-600"
    if (score >= 60) return "text-orange-600"
    return "text-red-600"
  }

  const teamScore = getTeamScore(team.name)

  const handleQuickAction = (actionType: string) => {
    const actionMessages = {
      stress: "How can we address stress and prevent burnout in our team?",
      motivation: "What strategies can boost team motivation and engagement?",
      timeline: "Help us manage deadlines and timeline pressure more effectively",
      wellness: "What wellness initiatives would benefit our team?",
      communication: "How can we improve team communication and collaboration?",
      workload: "Help us optimize workload distribution and prevent overwhelm",
    }

    setUserInput(actionMessages[actionType as keyof typeof actionMessages] || "")
    // Automatically send the message
    setTimeout(() => handleSendMessage(), 100)
  }

  const handleSendMessage = () => {
    if (!userInput.trim()) return

    const newMessages = [...chatMessages, { type: "user", message: userInput }]

    let botResponse = ""
    const recommendations = getActionRecommendations(team.name, team.riskLevel)
    const teamEmotions = getTeamEmotionData(team.name)

    if (userInput.toLowerCase().includes("timeline") || userInput.toLowerCase().includes("deadline")) {
      botResponse = `For ${team.name} team's timeline challenges, I recommend:\n\n`
      if (team.name === "Marketing") {
        botResponse +=
          "• Implement campaign timeline buffers (add 20% extra time for creative review cycles)\n• Create 'Campaign Checkpoint' meetings to catch scope creep early\n• Establish clear approval processes to prevent last-minute changes"
      } else if (team.name === "Engineering") {
        botResponse +=
          "• Break down complex API integrations into smaller, manageable sprints\n• Implement 'Definition of Done' boundaries to prevent scope expansion\n• Create technical debt sprints to address underlying issues causing delays"
      } else {
        botResponse +=
          "• Review current commitments and identify opportunities to redistribute workload\n• Implement milestone-based planning with regular check-ins\n• Create buffer time for unexpected challenges"
      }
    } else if (userInput.toLowerCase().includes("stress") || userInput.toLowerCase().includes("burnout")) {
      botResponse = `To address ${team.name} team's stress patterns, here are immediate actions:\n\n`
      if (team.name === "Support") {
        botResponse +=
          "• Implement ticket triage system to prevent overwhelming individual team members\n• Create 'Support Buddy System' for handling difficult customer escalations\n• Establish daily stress check-ins and provide mental health resources"
      } else if (team.name === "Marketing") {
        botResponse +=
          "• Introduce 'Marketing Wellness Wednesdays' with stress reduction activities\n• Create campaign post-mortems focused on learning, not blame\n• Implement workload audits to redistribute responsibilities more evenly"
      } else {
        botResponse +=
          "• Schedule immediate 1:1s to understand individual stress factors\n• Implement wellness check-ins and provide mental health support\n• Review workload distribution and adjust as needed"
      }
    } else if (userInput.toLowerCase().includes("motivation") || userInput.toLowerCase().includes("engagement")) {
      botResponse = `To boost ${team.name} team's motivation, focus on these strategies:\n\n`
      if (team.name === "Product") {
        botResponse +=
          "• Create customer success story sharing sessions to connect work to user impact\n• Implement 'Product Innovation Labs' for exploring new feature concepts\n• Establish product metrics dashboard to track and celebrate team impact"
      } else if (team.name === "Design") {
        botResponse +=
          "• Organize design showcase events to highlight creative achievements\n• Create design portfolio development time for professional growth\n• Implement 'Design Excellence Awards' to recognize outstanding work"
      } else {
        botResponse +=
          "• Implement recognition programs to celebrate achievements\n• Provide growth opportunities and skill development\n• Connect daily work to broader organizational impact"
      }
    } else if (userInput.toLowerCase().includes("tracker") || userInput.toLowerCase().includes("emotion")) {
      const primaryEmotion = teamEmotions[0]?.emotion || team.recentEmotions[0]
      botResponse = `Based on ${team.name} team's primary emotion (${primaryEmotion}) and associated trackers:\n\n`

      if (primaryEmotion.includes("Anxiety") || primaryEmotion.includes("Distress")) {
        botResponse +=
          "• Address root causes of anxiety through process improvements and clearer expectations\n• Provide emotional support through regular check-ins and peer support systems\n• Implement stress management training and wellness resources"
      } else if (primaryEmotion.includes("Excitement") || primaryEmotion.includes("Pride")) {
        botResponse +=
          "• Leverage positive energy by expanding team's mentoring role\n• Create opportunities to share successes and best practices\n• Provide advanced development opportunities to maintain engagement"
      } else if (primaryEmotion.includes("Tiredness") || primaryEmotion.includes("Anger")) {
        botResponse +=
          "• Implement immediate workload relief and process improvements\n• Address systemic issues causing frustration\n• Provide additional resources and support for team wellbeing"
      } else {
        botResponse +=
          "• Monitor emotional patterns and provide appropriate support\n• Maintain positive momentum through recognition and growth opportunities\n• Address any emerging concerns proactively"
      }
    } else {
      botResponse = `Here are the top people management actions for ${team.name} team (${team.riskLevel} risk level):\n\n`
      botResponse += "• " + recommendations.slice(0, 4).join("\n• ")

      botResponse += `\n\nThese recommendations are based on your team's current emotional patterns: ${teamEmotions.map((e) => e.emotion).join(", ")} and their associated workplace challenges.`
    }

    newMessages.push({ type: "bot", message: botResponse })
    setChatMessages(newMessages)
    setUserInput("")
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Overview
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{team.name} Team</h1>
              <Badge variant="outline" className={`text-lg font-bold px-3 py-1 ${getScoreColor(teamScore)}`}>
                {teamScore}%
              </Badge>
            </div>
            <p className="text-muted-foreground flex items-center gap-2 mt-1">
              <Users className="h-4 w-4" />
              {team.memberCount} members
            </p>
          </div>

          <Button onClick={() => setShowActionPlan(true)} className="gap-2">
            <MessageCircle className="h-4 w-4" />
            Create Action Plan
          </Button>
        </div>

        <div className="mt-6 pt-6 border-t">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Communication Methods (Last 30 Days)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Video className="h-5 w-5 text-primary" />
                <div className="text-2xl font-bold text-primary">{getTeamCommStats(team.name).meetings.total}</div>
                <div className="text-sm text-muted-foreground">
                  ({getTeamCommStats(team.name).meetings.monthlyAvg}/mo)
                </div>
              </div>
              <div className="text-xs text-muted-foreground">Meeting Minutes</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <MessageCircle className="h-5 w-5 text-blue-600" />
                <div className="text-2xl font-bold text-blue-600">{getTeamCommStats(team.name).chat.total}</div>
                <div className="text-sm text-muted-foreground">({getTeamCommStats(team.name).chat.monthlyAvg}/mo)</div>
              </div>
              <div className="text-xs text-muted-foreground">Chat Messages</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Mail className="h-5 w-5 text-green-600" />
                <div className="text-2xl font-bold text-green-600">{getTeamCommStats(team.name).email.total}</div>
                <div className="text-sm text-muted-foreground">({getTeamCommStats(team.name).email.monthlyAvg}/mo)</div>
              </div>
              <div className="text-xs text-muted-foreground">Emails Sent</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Plan Chat Dialog */}
      <Dialog open={showActionPlan} onOpenChange={setShowActionPlan}>
        <DialogContent className="max-w-7xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Action Plan for {team.name} Team</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 p-4 bg-muted/20 rounded-lg">
            {chatMessages.map((msg, index) => (
              <div key={index} className={`flex gap-3 ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`flex gap-2 max-w-[85%] ${msg.type === "user" ? "flex-row-reverse" : "flex-row"}`}>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${msg.type === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                  >
                    {msg.type === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div
                    className={`p-3 rounded-lg ${msg.type === "user" ? "bg-primary text-primary-foreground" : "bg-background border"}`}
                  >
                    <p className="text-sm whitespace-pre-line">{msg.message}</p>
                    {index === 0 && msg.type === "bot" && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs text-muted-foreground mb-3">Quick paths you can take:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md">
                          {team.riskLevel === "at-risk" ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("stress")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                🧘 Address Stress
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("workload")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                ⚖️ Balance Workload
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("wellness")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                💚 Team Wellness
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("timeline")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                ⏰ Timeline Help
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("motivation")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                🚀 Boost Motivation
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("communication")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                💬 Improve Communication
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("wellness")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                💚 Enhance Wellness
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickAction("workload")}
                                className="text-xs h-10 justify-start px-3 w-full"
                              >
                                📈 Optimize Performance
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Input
              placeholder="Ask about specific actions or concerns..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            />
            <Button onClick={handleSendMessage} size="sm">
              <Send className="h-4 w-4" />
            </Button>
          </div>

          <div className="pt-2">
            <p className="text-xs text-muted-foreground">
              Suggested actions based on {team.name} team's current {team.riskLevel} risk level and emotional patterns.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content Tabs */}
      <Tabs defaultValue="emotions" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="emotions"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-gray-200 transition-all duration-200"
          >
            Top Emotions
          </TabsTrigger>
          <TabsTrigger
            value="initiatives"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-gray-200 transition-all duration-200"
          >
            Key Initiatives
          </TabsTrigger>
          <TabsTrigger
            value="members"
            className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-gray-200 transition-all duration-200"
          >
            Team Communications
          </TabsTrigger>
        </TabsList>

        {/* Key Initiatives Tab */}
        <TabsContent value="initiatives" className="space-y-6">
          <Card className="p-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>Key Initiatives</CardTitle>
              <p className="text-sm text-muted-foreground">
                Track how your team is emotionally responding to major organizational events and initiatives.
              </p>
            </CardHeader>
            <CardContent className="px-0">
              {loadingTrackers ? (
                <div className="text-center py-8 text-muted-foreground">Loading tracker data...</div>
              ) : trackerAggregates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No tracker data available for this team. Showing example initiatives below.
                </div>
              ) : null}

              <div className="space-y-4">
                {(trackerAggregates.length > 0 ? trackerAggregates : getTeamInitiatives(team.name)).map((item: any, index: number) => {
                  const isTrackerData = trackerAggregates.length > 0
                  const rawPositivity =
                    isTrackerData && typeof item.positivity_percentage === "number" && !Number.isNaN(item.positivity_percentage)
                      ? item.positivity_percentage
                      : null
                  const initiative = isTrackerData
                    ? {
                        id: item._id || index.toString(),
                        name: item.tracker_name || "Unknown Tracker",
                        department: team.name,
                        dateRange:
                          item.start_date && item.end_date
                            ? `${new Date(item.start_date).toLocaleDateString()} - ${new Date(item.end_date).toLocaleDateString()}`
                            : null,
                        emotions:
                          item.top_emotions?.slice(0, 3).map((e: any) => e.emotion.charAt(0).toUpperCase() + e.emotion.slice(1)) || [],
                        positivityScore: rawPositivity !== null ? Math.round(rawPositivity) : null,
                        trend: item.trend || "stable",
                        hasMessages: item.total_hits > 0,
                        hasVideo: false,
                      }
                    : item

                  const trackerId = isTrackerData ? item.tracker_id || item.trackerId || item._id || item.id : null
                  const initiativeKey = String(trackerId ?? initiative.id ?? index)
                  const sanitizedKey = initiativeKey.replace(/[^a-zA-Z0-9_-]/g, "-")
                  const isExpanded = expandedInitiativeId === initiativeKey
                  const hitsForInitiative = initiativeHits[initiativeKey] || []
                  const isLoadingHits = initiativeLoading[initiativeKey]
                  const loadError = initiativeError[initiativeKey]

                  return (
                    <Card
                      key={initiativeKey}
                      className={`p-5 transition-shadow border-l-4 ${
                        isTrackerData ? "cursor-pointer hover:shadow-md" : ""
                      } ${isExpanded ? "bg-gray-50/80" : ""}`}
                      style={{
                        borderLeftColor:
                          typeof initiative.positivityScore === "number"
                            ? initiative.positivityScore >= 80
                              ? "#22c55e"
                              : initiative.positivityScore >= 60
                                ? "#f59e0b"
                                : "#ef4444"
                            : "#d1d5db",
                      }}
                      onClick={() => handleInitiativeClick(initiativeKey, trackerId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault()
                          handleInitiativeClick(initiativeKey, trackerId)
                        }
                      }}
                      role={isTrackerData ? "button" : undefined}
                      tabIndex={isTrackerData ? 0 : -1}
                      aria-expanded={isExpanded}
                      aria-controls={isExpanded ? `initiative-${sanitizedKey}` : undefined}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold">{initiative.name}</h3>
                            <Badge variant="outline" className="text-xs">
                              {initiative.department}
                            </Badge>
                            {initiative.dateRange && (
                              <Badge variant="outline" className="text-xs">
                                {initiative.dateRange}
                              </Badge>
                            )}
                            {isTrackerData && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {item.total_hits} hits
                              </Badge>
                            )}
                            <div className="flex items-center gap-1 ml-2">
                              {initiative.hasMessages && <MessageCircle className="h-4 w-4 text-gray-400" />}
                              {initiative.hasVideo && <Video className="h-4 w-4 text-gray-400" />}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {initiative.emotions.map((emotion: string, idx: number) => (
                              <Badge key={idx} variant="secondary" className={`text-xs ${getInitiativeEmotionBadgeColor(emotion)}`}>
                                {emotion}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-6">
                          <div className="text-right">
                            <div
                              className={`text-4xl font-bold ${
                                typeof initiative.positivityScore === "number"
                                  ? initiative.positivityScore >= 80
                                    ? "text-green-600"
                                    : initiative.positivityScore >= 60
                                      ? "text-yellow-600"
                                      : "text-red-600"
                                  : "text-gray-500"
                              }`}
                            >
                              {typeof initiative.positivityScore === "number" ? `${initiative.positivityScore}%` : "—"}
                            </div>
                            <Badge variant="outline" className={`text-xs mt-2 ${getInitiativeTrendBadgeColor(initiative.trend)}`}>
                              <span className="flex items-center gap-1">
                                {getInitiativeTrendIcon(initiative.trend)}
                                {initiative.trend}
                              </span>
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div id={`initiative-${sanitizedKey}`} className="mt-4 pt-3 border-t border-gray-200">
                          {isTrackerData ? (
                            isLoadingHits ? (
                              <div className="py-2 text-sm text-muted-foreground">Loading recent tracker activity…</div>
                            ) : loadError ? (
                              <div className="py-2 text-sm text-red-600">{loadError}</div>
                            ) : hitsForInitiative.length > 0 ? (
                              <div className="space-y-3">
                                {hitsForInitiative.map((hit: any, hitIndex: number) => {
                                  const description = getHitDescription(hit)
                                  const occurredAt =
                                    hit.occurred_at || hit.matched_at || hit.matchedAt || hit.evaluated_at || null
                                  const { label, Icon } = getContentTypeDisplay(
                                    hit.content_details?.type || hit.content_type || hit.source,
                                  )
                                  const borderColor = getHitBorderColor(hit.primary_emotion?.emotion)
                                  const relativeTime = formatTimeAgo(occurredAt || undefined)
                                  const absoluteTime = occurredAt
                                    ? new Date(occurredAt).toLocaleString(undefined, {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                      })
                                    : undefined

                                  return (
                                    <div
                                      key={hit.hit_id || hit.id || `hit-${hitIndex}`}
                                      className="rounded-md border border-gray-100 bg-white shadow-sm"
                                    >
                                      <div
                                        className="pl-4 pr-4 py-3"
                                        style={{ borderLeft: `4px solid ${borderColor}` }}
                                      >
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="flex items-center gap-2">
                                            {Icon && <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                                            <h5 className="font-medium text-sm">{label}</h5>
                                          </div>
                                          <span
                                            className="text-xs text-muted-foreground whitespace-nowrap"
                                            title={absoluteTime}
                                          >
                                            {relativeTime}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-700 mt-2">
                                          {description || "This tracker hit does not include a description."}
                                        </p>
                                        {hit.primary_emotion?.emotion && (
                                          <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <Badge
                                              variant="outline"
                                              className={`text-[11px] ${getInitiativeEmotionBadgeColor(
                                                hit.primary_emotion.emotion,
                                              )}`}
                                            >
                                              {hit.primary_emotion.emotion}
                                            </Badge>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="py-2 text-sm text-muted-foreground">
                                No recent tracker hits are available for this initiative.
                              </div>
                            )
                          ) : (
                            <div className="py-2 text-sm text-muted-foreground">
                              This is sample data. Connect a live tracker to see recent insights here.
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Emotions Tab */}
        <TabsContent value="emotions" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Top 3 Emotions</h3>
            <div className="flex flex-wrap gap-3 mb-8">
              {team.recentEmotions.slice(0, 3).map((emotion, index) => {
                // Get emotion color based on valence
                const getEmotionValence = (emotion: string) => {
                  const emotionValence: { [key: string]: number } = {
                    Concentration: 0.3,
                    Determination: 0.6,
                    Satisfaction: 0.9,
                    Excitement: 0.9,
                    Pride: 0.7,
                    Triumph: 0.9,
                    "Aesthetic Appreciation": 0.7,
                    Contemplation: 0.2,
                    Interest: 0.5,
                    Anxiety: -0.7,
                    Distress: -0.7,
                    Doubt: -0.4,
                    Anger: -0.8,
                    Tiredness: -0.3,
                    Frustration: -0.5,
                  }
                  return emotionValence[emotion] || 0
                }

                const valence = getEmotionValence(emotion)
                const badgeColor =
                  valence < -0.2
                    ? "border-red-200 text-red-800 bg-red-50"
                    : valence >= -0.2 && valence <= 0.3
                      ? "border-purple-200 text-purple-800 bg-purple-50"
                      : "border-green-200 text-green-800 bg-green-50"

                return (
                  <Badge key={index} variant="outline" className={`px-4 py-2 text-base border-2 ${badgeColor}`}>
                    {emotion}
                  </Badge>
                )
              })}
            </div>

            <h3 className="text-xl font-semibold mb-4">Team Trajectory Summary</h3>

            {team.trajectorySummary ? (
              <>
                {/* Overall Summary */}
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  {team.trajectorySummary.overall_summary}
                </p>

                {/* Top Emotions Analysis */}
                <ul className="space-y-6">
                  {team.trajectorySummary.top_emotions_analysis.map((analysis, index) => (
                    <li key={index} className="text-base text-gray-700 leading-relaxed">
                      <strong>{analysis.emotion}</strong> {analysis.team_dynamics}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                {/* Fallback UI when no trajectory summary is available */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <p className="text-sm text-blue-900 italic">
                    Trajectory summary is being generated. Check back after 6:00 AM PST for AI-generated insights.
                  </p>
                </div>
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  Overall positivity has {team.trend === "down" ? "declined" : team.trend === "up" ? "increased" : "remained stable"} for the{" "}
                  {team.name} team, driven by {team.recentEmotions.slice(0, 2).join(" and ").toLowerCase()} in team communications. The team's
                  emotional state reflects {team.positivityScore >= 70 ? "a generally positive" : team.positivityScore >= 50 ? "a mixed" : "concerning"}{" "}
                  tone in communications addressing {team.topTheme}.
                </p>

                <ul className="space-y-4">
                  {team.recentEmotions.slice(0, 3).map((emotion, index) => (
                    <li key={index} className="text-base text-gray-700 leading-relaxed">
                      <strong>{emotion}</strong> is {index === 0 ? "the dominant" : "a prominent"} emotion
                      {teamEmotionData[index] ? `, with extensive discussions about ${teamEmotionData[index].themes.slice(0, 2).join(" and ")}` : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </TabsContent>

        {/* Team Communications Tab */}
        <TabsContent value="members" className="space-y-6">
          <Card className="p-6">
            <CardHeader className="px-0 pt-0">
              <CardTitle>Inter-Team Communication Analysis</CardTitle>
              <p className="text-sm text-muted-foreground">
                Communication patterns, sentiment, and themes with other teams
              </p>
            </CardHeader>
            <CardContent className="px-0">
              {/* Communication Volume Chart */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Communication Volume by Team</h3>
                <div className="space-y-3">
                  {[
                    {
                      team: "Product Team",
                      volume: 85,
                      channels: ["Slack", "Email", "Meetings"],
                      sentiment: "Positive",
                      color: "bg-blue-500",
                    },
                    {
                      team: "Engineering Team",
                      volume: 72,
                      channels: ["Slack", "Meetings"],
                      sentiment: "Neutral",
                      color: "bg-green-500",
                    },
                    {
                      team: "Sales Team",
                      volume: 58,
                      channels: ["Email", "Meetings"],
                      sentiment: "Positive",
                      color: "bg-purple-500",
                    },
                    {
                      team: "Support Team",
                      volume: 45,
                      channels: ["Slack", "Email"],
                      sentiment: "Concerned",
                      color: "bg-orange-500",
                    },
                    {
                      team: "Leadership",
                      volume: 32,
                      channels: ["Email", "Meetings"],
                      sentiment: "Positive",
                      color: "bg-indigo-500",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="group cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedTeamComm(item)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{item.team}</span>
                          <div className="flex gap-1">
                            {item.channels.includes("Slack") && <MessageCircle className="h-4 w-4 text-gray-500" />}
                            {item.channels.includes("Email") && <Mail className="h-4 w-4 text-gray-500" />}
                            {item.channels.includes("Meetings") && <Video className="h-4 w-4 text-gray-500" />}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={`text-xs ${getSentimentBadgeColor(item.sentiment)}`}>
                            {item.sentiment}
                          </Badge>
                          <span className="text-sm font-medium">{item.volume}</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${item.color} transition-all duration-300 group-hover:opacity-80`}
                          style={{ width: `${item.volume}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Communication Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="h-5 w-5 text-blue-500" />
                    <h4 className="font-semibold">Most Active Channel</h4>
                  </div>
                  <p className="text-2xl font-bold">Slack</p>
                  <p className="text-sm text-muted-foreground">67% of communications</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <h4 className="font-semibold">Communication Trend</h4>
                  </div>
                  <p className="text-2xl font-bold text-green-600">+12%</p>
                  <p className="text-sm text-muted-foreground">vs last month</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    <h4 className="font-semibold">Teams Engaged</h4>
                  </div>
                  <p className="text-2xl font-bold">5</p>
                  <p className="text-sm text-muted-foreground">active collaborations</p>
                </Card>
              </div>

              {/* Top Communication Themes */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Top Communication Themes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      theme: "Project Coordination",
                      frequency: 34,
                      sentiment: "Positive",
                      teams: ["Product", "Engineering"],
                    },
                    {
                      theme: "Resource Planning",
                      frequency: 28,
                      sentiment: "Neutral",
                      teams: ["Product", "Leadership"],
                    },
                    { theme: "Customer Feedback", frequency: 22, sentiment: "Concerned", teams: ["Sales", "Support"] },
                    { theme: "Technical Discussions", frequency: 19, sentiment: "Positive", teams: ["Engineering"] },
                  ].map((theme, index) => (
                    <Card key={index} className="p-4 cursor-pointer hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{theme.theme}</h4>
                        <Badge variant="secondary" className={`text-xs ${getSentimentBadgeColor(theme.sentiment)}`}>
                          {theme.sentiment}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {theme.frequency} mentions • {theme.teams.join(", ")}
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                          className="h-1 rounded-full bg-blue-500"
                          style={{ width: `${(theme.frequency / 34) * 100}%` }}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
